import { useEffect, useMemo, useState } from "react";
import { fnbAPI, fmtINR, TABLE_STATUS_STYLE, LINE_STATUS_STYLE } from "./fnbApi";
import { toast } from "sonner";
import {
  Search, Plus, Minus, X, ShoppingCart, Receipt, Wallet,
  ArrowLeftRight, Bed, Grid3x3, Printer,
} from "lucide-react";
import { docsAPI } from "@/lib/documents";
import { notifyDocument } from "@/components/documents/documentToast";

/**
 * Reusable POS component. Renders menu grid + cart + order actions.
 * Props:
 *  - serviceKind: 'restaurant' | 'pool' | 'room_service' | 'bar_counter'
 *  - showTables: bool — enable table picker
 *  - showRoomInput: bool — enable room number input (room_service / pool)
 *  - filterAlcoholic: null | true | false
 */

/* ── Demo dataset (only when ?demo=1) ──────────────────────────────
 * Renders a realistic populated POS for the marketing screenshots.
 * All network calls are skipped — cannot leak into a real hotel.
 */
const DEMO_CATEGORIES = [
  { id: "d-cat-1", name: "Chef's Picks",  sort_order: 1 },
  { id: "d-cat-2", name: "Mains",          sort_order: 2 },
  { id: "d-cat-3", name: "Sides",          sort_order: 3 },
  { id: "d-cat-4", name: "Beverages",      sort_order: 4 },
  { id: "d-cat-5", name: "Desserts",       sort_order: 5 },
];
const DEMO_ITEMS = [
  { id: "d-i-1",  name: "Butter Chicken",       category_id: "d-cat-1", price: 480, tax_pct: 5, is_alcoholic: false, is_available: true, description: "Slow-simmered, cream-tomato base" },
  { id: "d-i-2",  name: "Truffle Risotto",       category_id: "d-cat-1", price: 620, tax_pct: 5, is_alcoholic: false, is_available: true, description: "Arborio rice · shaved truffle" },
  { id: "d-i-3",  name: "Wood-fired Margherita", category_id: "d-cat-2", price: 420, tax_pct: 5, is_alcoholic: false, is_available: true, description: "San Marzano · buffalo mozzarella" },
  { id: "d-i-4",  name: "Grilled Sea Bass",      category_id: "d-cat-2", price: 780, tax_pct: 5, is_alcoholic: false, is_available: true, description: "Lemon-caper butter, seasonal veg" },
  { id: "d-i-5",  name: "Dal Makhani",           category_id: "d-cat-2", price: 320, tax_pct: 5, is_alcoholic: false, is_available: true },
  { id: "d-i-6",  name: "Garlic Naan",           category_id: "d-cat-3", price: 90,  tax_pct: 5, is_alcoholic: false, is_available: true },
  { id: "d-i-7",  name: "Caesar Salad",          category_id: "d-cat-3", price: 260, tax_pct: 5, is_alcoholic: false, is_available: true },
  { id: "d-i-8",  name: "Cold Brew Coffee",      category_id: "d-cat-4", price: 180, tax_pct: 5, is_alcoholic: false, is_available: true },
  { id: "d-i-9",  name: "Fresh Lime Soda",       category_id: "d-cat-4", price: 120, tax_pct: 5, is_alcoholic: false, is_available: true },
  { id: "d-i-10", name: "Chocolate Fondant",     category_id: "d-cat-5", price: 340, tax_pct: 5, is_alcoholic: false, is_available: true, description: "Molten centre, vanilla ice cream" },
  { id: "d-i-11", name: "Tiramisu",              category_id: "d-cat-5", price: 300, tax_pct: 5, is_alcoholic: false, is_available: true },
  { id: "d-i-12", name: "House Cheesecake",      category_id: "d-cat-5", price: 320, tax_pct: 5, is_alcoholic: false, is_available: true },
];
const DEMO_TABLES = [
  { id: "d-t-1", table_number: "T-1",  seats: 2, status: "occupied", current_order_id: null },
  { id: "d-t-2", table_number: "T-2",  seats: 4, status: "occupied", current_order_id: null },
  { id: "d-t-3", table_number: "T-3",  seats: 2, status: "available", current_order_id: null },
  { id: "d-t-4", table_number: "T-4",  seats: 6, status: "occupied", current_order_id: null },
  { id: "d-t-5", table_number: "T-5",  seats: 4, status: "reserved",  current_order_id: null },
  { id: "d-t-6", table_number: "T-6",  seats: 2, status: "available", current_order_id: null },
];

/** Populate every POS state slice with realistic demo values. */
function hydrateDemoState({ setCategories, setItems, setTables, setOrders,
                            setActiveCat, setCart, setTableId, setRoomNumber,
                            setGuestName, setIsVip, setIsRush, setAllergyNotes,
                            setChargeToRoom }) {
  setCategories(DEMO_CATEGORIES);
  setItems(DEMO_ITEMS);
  setTables(DEMO_TABLES);
  setOrders([]);
  setActiveCat(null);
  // Pre-fill an active order so the sidebar showcases every enrichment field.
  setCart([
    { menu_item_id: "d-i-1",  name: "Butter Chicken",   price: 480, qty: 1 },
    { menu_item_id: "d-i-6",  name: "Garlic Naan",      price:  90, qty: 3 },
    { menu_item_id: "d-i-5",  name: "Dal Makhani",      price: 320, qty: 1 },
    { menu_item_id: "d-i-10", name: "Chocolate Fondant",price: 340, qty: 2 },
  ]);
  setTableId("d-t-2");
  setRoomNumber("412");
  setGuestName("Mrs. A. Kapoor");
  setIsVip(true);
  setIsRush(false);
  setAllergyNotes("Guest is nut-allergic — no cross-contact.");
  setChargeToRoom(true);
}

export default function FnbPOS({
  serviceKind = "restaurant",
  showTables = true,
  showRoomInput = false,
  filterAlcoholic = null,
  testidPrefix = "pos",
}) {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cart, setCart] = useState([]);
  const [tableId, setTableId] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [roomErr, setRoomErr] = useState("");
  const [guestName, setGuestName] = useState("");
  const [isVip, setIsVip] = useState(false);
  const [isRush, setIsRush] = useState(false);
  const [allergyNotes, setAllergyNotes] = useState("");
  const [chargeToRoom, setChargeToRoom] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [lastClosed, setLastClosed] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);

  const printReceipt = async () => {
    if (!lastClosed) return;
    setReceiptBusy(true);
    try {
      const rec = await docsAPI.createReceipt({ order_id: lastClosed.id });
      notifyDocument(`Receipt ${rec.receipt_number}`, { url: docsAPI.receiptPdfUrl(rec.id), filename: `Receipt-${rec.receipt_number}.pdf`, testId: "pos-receipt-toast" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not issue receipt"); }
    finally { setReceiptBusy(false); }
  };

  const isDemo = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("demo") === "1";

  const load = () => {
    if (isDemo) { hydrateDemoState({ setCategories, setItems, setTables, setOrders, setActiveCat, setCart, setTableId, setRoomNumber, setGuestName, setIsVip, setIsRush, setAllergyNotes, setChargeToRoom }); return Promise.resolve(); }
    return Promise.all([
    fnbAPI.categories(), fnbAPI.menuItems({ available_only: true }),
    fnbAPI.tables(),
    fnbAPI.orders({ status: "open", service_location_kind: serviceKind, limit: 50 }),
  ]).then(([c, i, t, o]) => { setCategories(c); setItems(i); setTables(t); setOrders(o); });
  };

  useEffect(() => { load(); }, [serviceKind]);
  useEffect(() => {
    if (isDemo) return;
    if (selectedOrder) {
      fnbAPI.order(selectedOrder.id).then(setSelectedOrder).catch(() => {});
    }
  }, [selectedOrder?.id]);

  const filteredItems = useMemo(() => {
    let arr = items;
    if (filterAlcoholic === true)  arr = arr.filter(i => i.is_alcoholic);
    if (filterAlcoholic === false) arr = arr.filter(i => !i.is_alcoholic);
    if (activeCat)  arr = arr.filter(i => i.category_id === activeCat);
    if (searchQ)    arr = arr.filter(i => i.name.toLowerCase().includes(searchQ.toLowerCase()));
    return arr;
  }, [items, activeCat, searchQ, filterAlcoholic]);

  const cartTotal = useMemo(() => {
    let sub = 0, tax = 0;
    for (const l of cart) {
      const it = items.find(i => i.id === l.menu_item_id);
      if (!it) continue;
      const s = Number(it.price) * l.qty;
      sub += s; tax += (s * Number(it.tax_pct || 0) / 100);
    }
    return { sub, tax, total: sub + tax };
  }, [cart, items]);

  const addToCart = (item) => {
    setCart(prev => {
      const found = prev.find(x => x.menu_item_id === item.id);
      if (found) return prev.map(x => x.menu_item_id === item.id ? { ...x, qty: x.qty + 1 } : x);
      return [...prev, { menu_item_id: item.id, name: item.name, price: item.price, qty: 1 }];
    });
  };
  const changeQty = (id, delta) => setCart(prev => prev
    .map(x => x.menu_item_id === id ? { ...x, qty: Math.max(0, x.qty + delta) } : x)
    .filter(x => x.qty > 0));
  const removeItem = (id) => setCart(prev => prev.filter(x => x.menu_item_id !== id));

  const submitOrder = async () => {
    if (!cart.length) { toast.error("Cart is empty"); return; }
    if (showTables && !tableId && !selectedOrder && serviceKind === "restaurant") {
      toast.error("Pick a table first"); return;
    }
    if (showRoomInput && !roomNumber && !selectedOrder) {
      setRoomErr("Room number is required"); toast.error("Enter room number"); return;
    }
    const lines = cart.map(x => ({ menu_item_id: x.menu_item_id, qty: x.qty }));
    try {
      if (selectedOrder) {
        const upd = await fnbAPI.addLines(selectedOrder.id, lines);
        setSelectedOrder(upd);
        toast.success("Items added to order");
      } else {
        const body = { service_location_kind: serviceKind, lines,
          table_id: tableId || null,
          room_number: (chargeToRoom || serviceKind === "room_service") ? (roomNumber || null) : null,
          guest_name: guestName || null,
          is_vip: !!isVip,
          is_rush: !!isRush,
          has_allergy: !!(allergyNotes && allergyNotes.trim()),
          allergy_notes: (allergyNotes || "").trim() || null,
        };
        const created = await fnbAPI.createOrder(body);
        toast.success(`Order ${created.code} created${created.room_number ? ` · linked to Room ${created.room_number}` : ""}`);
        setSelectedOrder(created);
      }
      setCart([]);
      // Reset per-order flags but keep table/room context to speed up next order
      setIsVip(false); setIsRush(false); setAllergyNotes("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const closeOrder = async (payload) => {
    if (!selectedOrder) return;
    try {
      const doc = await fnbAPI.closeOrder(selectedOrder.id, payload);
      toast.success("Order closed · " + (doc.settlement?.charged_to_folio ? "Charged to room" : `Payment ${doc.settlement?.payment_code}`));
      setLastClosed(doc.settlement?.charged_to_folio ? null : doc);
      setSelectedOrder(null); setCart([]); setTableId(""); setRoomNumber(""); setGuestName("");
      setIsVip(false); setIsRush(false); setAllergyNotes(""); setChargeToRoom(false);
      setShowPay(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const voidOrder = async () => {
    if (!selectedOrder) return;
    const reason = window.prompt("Void reason:"); if (!reason) return;
    try {
      await fnbAPI.voidOrder(selectedOrder.id, reason);
      toast.success("Order voided");
      setSelectedOrder(null); setCart([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4" data-testid={`${testidPrefix}-root`}>
      {/* LEFT: Menu */}
      <div className="space-y-3">
        {/* search + cats */}
        <div className="bg-white border border-slate-200 rounded-2xl p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search menu…" aria-label="Search menu" data-testid={`${testidPrefix}-search`}
              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            <button onClick={() => setActiveCat(null)} data-testid={`${testidPrefix}-cat-all`}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${!activeCat ? "bg-hocp-mint text-hocp-deepTeal" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>All</button>
            {categories.map(c => (
              <button key={c.id} onClick={() => setActiveCat(c.id)} data-testid={`${testidPrefix}-cat-${c.name.replace(/\s+/g,'-').toLowerCase()}`}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-medium ${activeCat === c.id ? "bg-hocp-mint text-hocp-deepTeal" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>{c.name}</button>
            ))}
          </div>
        </div>

        {/* menu grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {filteredItems.length === 0 ? (
            <div className="col-span-full text-center py-10 text-[13px] text-slate-400" data-testid={`${testidPrefix}-menu-empty`}>
              No items found.
            </div>
          ) : filteredItems.map(m => (
            <button key={m.id} onClick={() => addToCart(m)} data-testid={`${testidPrefix}-item-${m.id}`}
              className="text-left bg-white border border-slate-200 rounded-xl p-3 hover:border-hocp-navy hover:shadow-md transition-all">
              <div className="text-[13px] font-medium text-slate-800 line-clamp-2 min-h-[36px]">{m.name}</div>
              <div className="mt-2 flex items-center justify-between">
                <div className="font-mono font-semibold text-hocp-navy text-[14px]">{fmtINR(m.price)}</div>
                {m.is_alcoholic && <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 font-semibold uppercase">21+</span>}
              </div>
              {m.prep_time_min != null && <div className="text-[10px] text-slate-400 mt-1">≈{m.prep_time_min} min</div>}
            </button>
          ))}
        </div>
      </div>

      {/* RIGHT: Cart + Open orders */}
      <aside className="space-y-3">
        {/* Receipt for the order just paid */}
        {lastClosed && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 flex items-center gap-3" data-testid={`${testidPrefix}-last-closed`}>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-medium">Payment collected</div>
              <div className="text-[12.5px] text-slate-700 truncate"><span className="font-mono">{lastClosed.code}</span> · {fmtINR(lastClosed.total)} · {lastClosed.guest_name || "Walk-in"}</div>
            </div>
            <button onClick={printReceipt} disabled={receiptBusy} data-testid={`${testidPrefix}-print-receipt`}
              className="px-3 py-2 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[12px] font-semibold hover:brightness-110 flex items-center gap-1.5 disabled:opacity-60">
              <Printer className="w-3.5 h-3.5"/> Print Receipt
            </button>
            <button onClick={() => setLastClosed(null)} className="text-slate-400 hover:text-slate-700" aria-label="Dismiss"><X className="w-4 h-4"/></button>
          </div>
        )}

        {/* Open orders switcher */}
        {orders.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-2">Open Orders ({orders.length})</div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {orders.map(o => (
                <button key={o.id} onClick={() => setSelectedOrder(o)} data-testid={`${testidPrefix}-open-${o.code}`}
                  className={`w-full text-left px-3 py-2 rounded-lg text-[12px] transition-colors ${
                    selectedOrder?.id === o.id ? "bg-hocp-mint text-hocp-deepTeal" : "bg-slate-50 hover:bg-slate-100 text-slate-700"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono">{o.code}</span>
                    <span className="font-mono">{fmtINR(o.total)}</span>
                  </div>
                  <div className="text-[10.5px] opacity-80">{o.guest_name || "—"} · {o.lines?.length || 0} items</div>
                </button>
              ))}
              {selectedOrder && (
                <button onClick={() => { setSelectedOrder(null); setCart([]); }} className="w-full text-[11px] text-slate-500 py-1 hover:text-slate-800">
                  ← Start new order
                </button>
              )}
            </div>
          </div>
        )}

        {/* Cart */}
        <div className="bg-white border border-slate-200 rounded-2xl">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-hocp-navy"/>
            <div className="font-display text-[14px] font-semibold text-hocp-navy">
              {selectedOrder ? `Add to ${selectedOrder.code}` : "New Order"}
            </div>
          </div>

          {/* Where/guest/priority inputs — only shown when NOT adding to an existing order */}
          {!selectedOrder && (
            <div className="p-3 border-b border-slate-100 space-y-2">
              {showTables && serviceKind === "restaurant" && (
                <div>
                  <label htmlFor={`${testidPrefix}-table-select`} className="block text-[11.5px] font-medium text-slate-600 mb-1">Table</label>
                  <select id={`${testidPrefix}-table-select`} value={tableId} onChange={e => setTableId(e.target.value)}
                    data-testid={`${testidPrefix}-table-select`}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] bg-white">
                    <option value="">Pick a table…</option>
                    {tables.filter(t => t.status === "available").map(t => (
                      <option key={t.id} value={t.id}>{t.table_number} · {t.capacity}p · {t.floor}</option>
                    ))}
                  </select>
                </div>
              )}
              {showRoomInput ? (
                <div>
                  <label htmlFor={`${testidPrefix}-room-input`} className="block text-[11.5px] font-medium text-slate-600 mb-1">Room number <span className="text-rose-500">*</span></label>
                  <input id={`${testidPrefix}-room-input`} value={roomNumber} onChange={e => { setRoomNumber(e.target.value); setRoomErr(""); }} onBlur={() => setRoomErr(roomNumber ? "" : "Room number is required")}
                    placeholder="e.g. 301" data-testid={`${testidPrefix}-room-input`}
                    className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] ${roomErr ? "border-rose-400 focus:ring-rose-200" : ""}`}/>
                  {roomErr && <p className="flex items-center gap-1 text-[11px] text-rose-500 mt-1" data-testid={`${testidPrefix}-room-input-error`}>{roomErr}</p>}
                </div>
              ) : (
                // Optional room-folio link for dine-in / bar / pool / etc.
                <div className="rounded-lg border border-slate-200 p-2 space-y-2">
                  <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={chargeToRoom}
                      onChange={e => setChargeToRoom(e.target.checked)}
                      data-testid={`${testidPrefix}-charge-to-room-toggle`}
                      className="rounded"/>
                    Charge to room folio (link this order to a guest room)
                  </label>
                  {chargeToRoom && (
                    <div>
                      <label htmlFor={`${testidPrefix}-room-input-optional`} className="block text-[11.5px] font-medium text-slate-600 mb-1">Room number</label>
                      <input id={`${testidPrefix}-room-input-optional`} value={roomNumber} onChange={e => setRoomNumber(e.target.value)}
                        placeholder="e.g. 301"
                        data-testid={`${testidPrefix}-room-input-optional`}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
                    </div>
                  )}
                </div>
              )}
              <div>
                <label htmlFor={`${testidPrefix}-guest-input`} className="block text-[11.5px] font-medium text-slate-600 mb-1">Guest name <span className="text-slate-400 font-normal">(optional)</span></label>
                <input id={`${testidPrefix}-guest-input`} value={guestName} onChange={e => setGuestName(e.target.value)}
                  placeholder="e.g. Mr. Sharma" data-testid={`${testidPrefix}-guest-input`}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"/>
              </div>
              {/* Priority toggles */}
              <div className="flex items-center gap-2 pt-1">
                <button type="button"
                        onClick={() => setIsVip(v => !v)}
                        data-testid={`${testidPrefix}-vip-toggle`}
                        className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                          isVip
                            ? "bg-purple-600 text-white border-purple-600"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}>
                  VIP
                </button>
                <button type="button"
                        onClick={() => setIsRush(v => !v)}
                        data-testid={`${testidPrefix}-rush-toggle`}
                        className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                          isRush
                            ? "bg-red-600 text-white border-red-600"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}>
                  RUSH
                </button>
              </div>

              {/* Allergy notes */}
              <div>
                <label htmlFor={`${testidPrefix}-allergy-input`} className="block text-[11.5px] font-medium text-slate-600 mb-1">Allergy / dietary notes <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  id={`${testidPrefix}-allergy-input`}
                  value={allergyNotes}
                  onChange={e => setAllergyNotes(e.target.value)}
                  placeholder="e.g. Nut allergy — no cross-contact"
                  data-testid={`${testidPrefix}-allergy-input`}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] resize-none"/>
              </div>
            </div>
          )}

          {/* Cart lines */}
          <div className="p-3 space-y-1 max-h-64 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="text-center py-6 text-[12px] text-slate-400" data-testid={`${testidPrefix}-cart-empty`}>
                Tap items on the left to add.
              </div>
            ) : cart.map(l => (
              <div key={l.menu_item_id} className="flex items-center gap-2 py-1.5" data-testid={`${testidPrefix}-cart-${l.menu_item_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-slate-800 truncate">{l.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{fmtINR(l.price)} × {l.qty} = {fmtINR(Number(l.price) * l.qty)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => changeQty(l.menu_item_id, -1)} className="w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                    <Minus className="w-3 h-3"/>
                  </button>
                  <span className="w-6 text-center tabular-nums text-[13px] font-semibold">{l.qty}</span>
                  <button onClick={() => changeQty(l.menu_item_id, 1)} className="w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
                    <Plus className="w-3 h-3"/>
                  </button>
                  <button onClick={() => removeItem(l.menu_item_id)} className="w-6 h-6 rounded-md text-rose-500 hover:bg-rose-50 flex items-center justify-center ml-1">
                    <X className="w-3 h-3"/>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          {cart.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-[12px] space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono tabular-nums">{fmtINR(cartTotal.sub)}</span></div>
              <div className="flex justify-between"><span>Tax</span><span className="font-mono tabular-nums">{fmtINR(cartTotal.tax)}</span></div>
              <div className="flex justify-between text-[14px] font-semibold text-hocp-navy pt-1 border-t border-slate-200"><span>Total</span><span className="font-mono tabular-nums">{fmtINR(cartTotal.total)}</span></div>
            </div>
          )}

          {/* Actions */}
          <div className="p-3 flex gap-2 border-t border-slate-100">
            <button onClick={submitOrder} disabled={!cart.length} data-testid={`${testidPrefix}-submit`}
              className="flex-1 px-4 py-2.5 rounded-lg bg-hocp-mint text-hocp-deepTeal text-[13px] font-semibold hover:brightness-110 disabled:opacity-40">
              {selectedOrder ? "Send to Kitchen" : "Send Order"}
            </button>
            {selectedOrder && (
              <>
                <button onClick={() => setShowPay(true)} data-testid={`${testidPrefix}-pay`}
                  className="px-3 py-2.5 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold hover:brightness-110">
                  <Receipt className="w-4 h-4"/>
                </button>
                <button onClick={voidOrder} data-testid={`${testidPrefix}-void`}
                  title="Void" className="px-3 py-2.5 rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 text-[13px]">
                  <X className="w-4 h-4"/>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Selected order line status view */}
        {selectedOrder && selectedOrder.lines?.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-3 space-y-1.5" data-testid={`${testidPrefix}-order-lines`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-1">
              Order Lines · {selectedOrder.code}
            </div>
            {selectedOrder.lines.map(L => {
              const s = LINE_STATUS_STYLE[L.production_status] || LINE_STATUS_STYLE.new;
              return (
                <div key={L.line_id} className="flex items-center gap-2 text-[12px]">
                  <span className="flex-1 truncate text-slate-700">{L.qty}× {L.menu_item_name}</span>
                  <span className={`px-1.5 py-0.5 rounded font-medium text-[10px] ${s.chip}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </aside>

      {/* Payment dialog */}
      {showPay && selectedOrder && (
        <PayDialog order={selectedOrder} onClose={() => setShowPay(false)} onSubmit={closeOrder}/>
      )}
    </div>
  );
}

function PayDialog({ order, onClose, onSubmit }) {
  const [method, setMethod] = useState("cash");
  const [charge, setCharge] = useState(false);
  const [ref, setRef] = useState("");
  const [tip, setTip] = useState("");
  const [disc, setDisc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    const body = charge
      ? { charge_to_folio: true, reservation_id: order.reservation_id, folio_id: order.folio_id, tip: tip || undefined, discount: disc || undefined }
      : { method, reference_no: ref || undefined, tip: tip || undefined, discount: disc || undefined };
    try { await onSubmit(body); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fnb-pay-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Settle · {order.code}</div>
            <div className="font-display text-[24px] font-bold text-hocp-navy tabular-nums mt-1">{fmtINR(order.total)}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400"/></button>
        </div>
        <div className="p-6 space-y-4">
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={charge} onChange={e => setCharge(e.target.checked)}
              data-testid="fnb-pay-folio-toggle"/>
            <Bed className="w-4 h-4 text-slate-500"/>
            Charge to guest folio (settle on checkout)
          </label>
          {!charge && (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Method</label>
                <div className="grid grid-cols-4 gap-2 mt-1">
                  {["cash","card","upi","corporate"].map(m => (
                    <button key={m} onClick={() => setMethod(m)} data-testid={`fnb-pay-method-${m}`}
                      className={`px-3 py-2 rounded-lg text-[12px] font-medium border ${method===m?"bg-hocp-mint text-hocp-deepTeal border-hocp-mint":"bg-white border-slate-200 hover:bg-slate-50"}`}>
                      {m.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="fnb-pay-ref" className="block text-[11.5px] font-medium text-slate-600 mb-1">Reference / UTR <span className="text-slate-400 font-normal">(optional)</span></label>
                <input id="fnb-pay-ref" value={ref} onChange={e => setRef(e.target.value)}
                  placeholder="e.g. UPI txn no." data-testid="fnb-pay-ref"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="fnb-pay-disc" className="block text-[11.5px] font-medium text-slate-600 mb-1">Discount (₹)</label>
              <input id="fnb-pay-disc" value={disc} onChange={e => setDisc(e.target.value)} pattern="^\d+(\.\d{1,2})?$"
                placeholder="e.g. 50" data-testid="fnb-pay-disc"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
            <div>
              <label htmlFor="fnb-pay-tip" className="block text-[11.5px] font-medium text-slate-600 mb-1">Tip (₹)</label>
              <input id="fnb-pay-tip" value={tip} onChange={e => setTip(e.target.value)} pattern="^\d+(\.\d{1,2})?$"
                placeholder="e.g. 100" data-testid="fnb-pay-tip"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"/>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-[13px] font-medium text-slate-600">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="fnb-pay-submit"
              className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold disabled:opacity-60">
              {busy ? "Processing…" : (charge ? "Charge to Room" : "Take Payment")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
